import { Injectable } from '@angular/core';
import { DataService } from './data.service';

/** localStorage key holding the OU ids the signed-in user is report expert for. */
export const REPORT_EXPERT_OUS_KEY = 'reportExpertOus';

export interface ExpertUser {
  _id: string;
  name?: { first?: string; middle?: string; last?: string };
  fullName: string;
  email: string;
  image?: string;
  isActive?: boolean;
}

export interface ExpertAssignment {
  /** Assignment id, not the user id. */
  _id: string;
  ou: string;
  /** Null when the user record was deleted. */
  user: ExpertUser | null;
  createdAt?: string;
}

export interface OuExpertsOverview {
  ou: string;
  assigned: ExpertAssignment[];
  suggested: Array<ExpertUser & { isAssigned: boolean }>;
}

/** "first middle last" without the "undefined" gaps the old code produced. */
export function expertFullName(name: any, fallback = ''): string {
  if (!name) return fallback;
  if (typeof name === 'string') return name;
  const full = [name.first, name.middle, name.last]
    .filter((part: any) => typeof part === 'string' && part.trim() && part !== 'undefined' && part !== 'null')
    .join(' ')
    .trim();
  return full || fallback;
}

/**
 * Report expert assignments: who receives and signs each OU's periodic
 * content report. The backend enforces every rule; the cached OU list here
 * only decides what the UI shows.
 */
@Injectable({ providedIn: 'root' })
export class ContentReportExpertsService {
  private refreshing: Promise<string[]> | null = null;

  constructor(private dataService: DataService) {}

  /** Assigned + suggested experts for each OU. */
  getOverview(ous: string[]) {
    return this.dataService.postData('content-report-experts/overview', { ous });
  }

  /**
   * Assigned, active experts for each OU, mapped to the `{ _id, name, email }`
   * options the report dropdowns use. Returns null on a failed request.
   */
  async getAssignedForReports(ous: string[]): Promise<Record<string, Array<{ _id: string; name: string; email: string }>> | null> {
    if (!ous.length) return {};
    const res = await this.dataService.postData('content-report-experts/assigned', { ous });
    if (res.error || !res.data) return null;
    const result: Record<string, Array<{ _id: string; name: string; email: string }>> = {};
    Object.keys(res.data).forEach((ou) => {
      result[ou] = (res.data[ou] || []).map((user: any) => ({
        _id: user._id,
        name: user.fullName || expertFullName(user.name, user.email),
        email: user.email,
      }));
    });
    return result;
  }

  searchUsers(query: string) {
    return this.dataService.getData(`content-report-experts/users/search?q=${encodeURIComponent(query)}&limit=20`);
  }

  assign(ou: string, users: string[]) {
    return this.dataService.postData('content-report-experts', { ou, users });
  }

  reassign(assignmentId: string, user: string) {
    return this.dataService.postData(`content-report-experts/${assignmentId}`, { user }, 'PUT');
  }

  /** deleteData resolves to undefined when the request fails; normalize that to an error. */
  async remove(assignmentId: string): Promise<{ error: boolean; msg?: string; data?: any }> {
    const res = await this.dataService.deleteData(`content-report-experts/${assignmentId}`);
    return res ?? { error: true };
  }

  /** OU ids from the last refresh (synchronous, for guards and menus). */
  getCachedOuIds(): string[] {
    try {
      const raw = localStorage.getItem(REPORT_EXPERT_OUS_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  isReportExpert(): boolean {
    return this.getCachedOuIds().length > 0;
  }

  /** Re-reads the signed-in user's assignments and caches them. */
  refreshMyOuIds(): Promise<string[]> {
    if (!localStorage.getItem('isLoggedIn') || localStorage.getItem('isLoggedIn') === 'false') {
      return Promise.resolve([]);
    }
    if (!this.refreshing) {
      this.refreshing = this.dataService
        .getData('content-report-experts/mine')
        .then((res: any) => {
          if (res?.error || !Array.isArray(res?.data)) {
            return this.getCachedOuIds();
          }
          localStorage.setItem(REPORT_EXPERT_OUS_KEY, JSON.stringify(res.data));
          return res.data as string[];
        })
        .catch(() => this.getCachedOuIds())
        .finally(() => (this.refreshing = null));
    }
    return this.refreshing;
  }
}
